import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import EmailVerification from "./components/auth/EmailVerification";
import BoardList from "./components/boards/BoardList";
import BoardView from "./components/boards/BoardView";
import PublicBoardView from "./components/boards/PublicBoardView";
import { useAuth } from "./contexts/AuthContext";
import './css/PublicBoardGate.css';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

const ProtectedRoute = ({ children }) => {
    const { user, loading, emailVerified } = useAuth();
    if (loading) return <div className="LoadingScreen"><div className="LoadingScreen__spinner" /></div>;
    if (!user) return <Navigate to="/login" />;
    if (!emailVerified) return <Navigate to="/verify-email" />;
    return children;
};

/**
 * Guards /board/:boardId for unauthenticated visitors.
 * If the board is public, shows a gate screen offering "View as Guest" or "Sign In".
 * If the board is private or not found, redirects to /login.
 */
const BoardRouteGuard = () => {
    const { boardId, taskNumber } = useParams();
    const { user, loading, emailVerified } = useAuth();
    const navigate = useNavigate();
    const [publicBoard, setPublicBoard] = useState(null);
    const [checking, setChecking] = useState(false);
    const [checked, setChecked] = useState(false);

    useEffect(() => {
        if (!loading && !user) {
            setChecking(true);
            fetch(`${API_BASE}/tasks/boards/${boardId}/public/`)
                .then(res => (res.ok ? res.json() : null))
                .then(data => {
                    if (data) {
                        setPublicBoard(data);
                    } else {
                        navigate('/login', { replace: true });
                    }
                    setChecking(false);
                    setChecked(true);
                })
                .catch(() => {
                    navigate('/login', { replace: true });
                });
        }
    }, [loading, user, boardId, navigate]);

    if (loading || checking) {
        return <div className="LoadingScreen"><div className="LoadingScreen__spinner" /></div>;
    }

    // Authenticated and email verified → show the real board
    if (user && emailVerified) {
        return <BoardView />;
    }

    // Authenticated but email not verified
    if (user && !emailVerified) {
        return <Navigate to="/verify-email" />;
    }

    // Unauthenticated + public board confirmed
    if (checked && publicBoard) {
        return (
            <div className="PublicBoardGate">
                <div className="PublicBoardGate__navbar">
                    <span className="PublicBoardGate__brand">
                        Bluebird <span className="PublicBoardGate__brand-sub">Tasks</span>
                    </span>
                </div>
                <div className="PublicBoardGate__body">
                    <div className="PublicBoardGate__card">
                        <span className="PublicBoardGate__badge">
                            <i className="fas fa-globe" /> Public Board
                        </span>
                        <div className="PublicBoardGate__title-row">
                            {publicBoard.abbreviation && (
                                <span className="PublicBoardGate__abbr">{publicBoard.abbreviation}</span>
                            )}
                            <h1 className="PublicBoardGate__title">{publicBoard.name}</h1>
                        </div>
                        {publicBoard.description && (
                            <p className="PublicBoardGate__desc">{publicBoard.description}</p>
                        )}
                        {publicBoard.owner_name && (
                            <p className="PublicBoardGate__owner">by {publicBoard.owner_name}</p>
                        )}
                        <p className="PublicBoardGate__hint">
                            This board is publicly viewable. You can browse it as a guest, or sign in if you have access.
                        </p>
                        <div className="PublicBoardGate__actions">
                            <button
                                className="PublicBoardGate__btn PublicBoardGate__btn--primary"
                                onClick={() => navigate(`/public/board/${boardId}${taskNumber ? `/task/${taskNumber}` : ''}`)}
                            >
                                <i className="fas fa-globe" /> View Public Board
                            </button>
                            <button
                                className="PublicBoardGate__btn PublicBoardGate__btn--secondary"
                                onClick={() => navigate('/login', { state: { from: `/board/${boardId}${taskNumber ? `/task/${taskNumber}` : ''}` } })}
                            >
                                <i className="fas fa-sign-in-alt" /> Sign In
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

const Main = () => {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/public/board/:boardId" element={<PublicBoardView />} />
            <Route path="/public/board/:boardId/task/:taskNumber" element={<PublicBoardView />} />
            <Route path="/" element={
                <ProtectedRoute><BoardList /></ProtectedRoute>
            } />
            <Route path="/board/:boardId" element={<BoardRouteGuard />} />
            <Route path="/board/:boardId/task/:taskNumber" element={<BoardRouteGuard />} />
        </Routes>
    );
};

export default Main;
